/**
 * Read media duration for short clips. MediaRecorder WebM often reports Infinity
 * until seeked — use the Chromium seek hack, then optional fallbackSeconds.
 */

/**
 * @param {File|Blob} file
 * @param {{ fallbackSeconds?: number|null, timeoutMs?: number }} [options]
 * @returns {Promise<number>}
 */
export function readVideoDuration(file, { fallbackSeconds = null, timeoutMs = 8000 } = {}) {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('No video selected.'));
      return;
    }

    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    let settled = false;

    const cleanup = () => {
      video.onloadedmetadata = null;
      video.ontimeupdate = null;
      video.onseeked = null;
      video.onerror = null;
      video.removeAttribute('src');
      video.load();
      URL.revokeObjectURL(url);
    };

    const finish = (duration, fromFallback = false) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      cleanup();
      if (Number.isFinite(duration) && duration > 0) {
        resolve(duration);
        return;
      }
      if (Number.isFinite(fallbackSeconds) && fallbackSeconds > 0) {
        resolve(fallbackSeconds);
        return;
      }
      reject(new Error(fromFallback ? 'Could not read video duration.' : 'Could not read video duration.'));
    };

    const timer = window.setTimeout(() => {
      finish(NaN, true);
    }, timeoutMs);

    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;

    video.onerror = () => {
      if (Number.isFinite(fallbackSeconds) && fallbackSeconds > 0) {
        finish(fallbackSeconds);
        return;
      }
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      cleanup();
      reject(new Error('Could not read that video file.'));
    };

    video.onloadedmetadata = () => {
      const raw = video.duration;
      if (Number.isFinite(raw) && raw > 0 && raw !== Infinity) {
        finish(raw);
        return;
      }

      // MediaRecorder WebM often has Infinity until a seek forces the browser
      // to compute the real duration from buffered media.
      const onSeeked = () => {
        video.onseeked = null;
        video.ontimeupdate = null;
        const fixed = video.duration;
        if (Number.isFinite(fixed) && fixed > 0 && fixed !== Infinity) {
          video.currentTime = 0;
          finish(fixed);
          return;
        }
        finish(Number.isFinite(fallbackSeconds) ? fallbackSeconds : NaN, true);
      };

      video.onseeked = onSeeked;
      video.ontimeupdate = () => {
        // Some browsers fire timeupdate instead of seeked for the large seek.
        if (Number.isFinite(video.duration) && video.duration !== Infinity && video.duration > 0) {
          onSeeked();
        }
      };

      try {
        video.currentTime = 1e101;
      } catch {
        finish(Number.isFinite(fallbackSeconds) ? fallbackSeconds : NaN, true);
      }
    };

    video.src = url;
  });
}
